import React, { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Receipt as ReceiptIcon,
  List as ListIcon,
  BarChart as BarChartIcon,
  FileDownload as FileDownloadIcon,
  RequestQuote as RequestQuoteIcon,
  Business as BusinessIcon,
  AccountBalance as AccountBalanceIcon,
  Dashboard as DashboardIcon,
  SwapHoriz as SwapHorizIcon,
  CheckCircle as CheckCircleIcon,
  CompareArrows as CompareArrowsIcon,
  Logout as LogoutIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import { AuthService } from "../services/authService";
import ChangePasswordDialog from "./ChangePasswordDialog";

const drawerWidth = 280;
const miniDrawerWidth = 64;

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
}

const menuItems: MenuItem[] = [
  { text: "Lista de Faturas", icon: <ListIcon />, path: "/" },
  { text: "Estatísticas", icon: <BarChartIcon />, path: "/statistics" },
  { text: "Exportar", icon: <FileDownloadIcon />, path: "/export" },
  {
    text: "Relatório de Despesas",
    icon: <RequestQuoteIcon />,
    path: "/expense-report",
  },
  { text: "Empresas", icon: <BusinessIcon />, path: "/companies" },
  {
    text: "Extratos Bancários",
    icon: <AccountBalanceIcon />,
    path: "/bank-statements",
  },
  {
    text: "Matching Dashboard",
    icon: <DashboardIcon />,
    path: "/matching-dashboard",
  },
  {
    text: "Matching Manual",
    icon: <SwapHorizIcon />,
    path: "/manual-matching",
  },
  {
    text: "Matches Pendentes",
    icon: <CheckCircleIcon />,
    path: "/pending-matches",
  },
  {
    text: "Matching Avançado",
    icon: <CompareArrowsIcon />,
    path: "/advanced-matching",
  },
];

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isExpanded ? "center" : "center",
          px: [1],
          bgcolor: "primary.main",
          color: "white",
          minHeight: 64,
        }}
      >
        <ReceiptIcon sx={{ mr: isExpanded ? 1 : 0 }} />
        {isExpanded && (
          <Typography variant="h6" noWrap component="div">
            TinyInvoicer
          </Typography>
        )}
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <Tooltip
              title={!isExpanded ? item.text : ""}
              placement="right"
              arrow
            >
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: isExpanded ? "initial" : "center",
                  px: 2.5,
                  "&.Mui-selected": {
                    backgroundColor: "primary.light",
                    color: "primary.contrastText",
                    "&:hover": {
                      backgroundColor: "primary.light",
                    },
                    "& .MuiListItemIcon-root": {
                      color: "primary.contrastText",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isExpanded ? 3 : "auto",
                    justifyContent: "center",
                    color:
                      location.pathname === item.path
                        ? "primary.contrastText"
                        : "inherit",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {isExpanded && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: {
            md: `calc(100% - ${miniDrawerWidth}px)`,
          },
          ml: { md: `${miniDrawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Gestão de Faturas Portuguesas
          </Typography>
          <Tooltip title="Mudar Password">
            <IconButton color="inherit" onClick={() => setChangePasswordOpen(true)}>
              <VpnKeyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sair">
            <IconButton color="inherit" onClick={() => AuthService.logout()}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Drawer for Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Drawer for Desktop */}
      <Drawer
        variant="permanent"
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: isExpanded ? drawerWidth : miniDrawerWidth,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
            zIndex: isExpanded ? theme.zIndex.drawer + 1 : theme.zIndex.drawer,
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: { md: `${miniDrawerWidth}px` },
          width: {
            md: `calc(100% - ${miniDrawerWidth}px)`,
          },
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
          <Outlet />
        </Container>
      </Box>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </Box>
  );
};

export default Layout;
